import React, { useEffect, useState } from "react";

import { useHistory, useLocation, useParams } from "react-router-dom";

import CnaeViewEdit from "../../../../../../presentation/pages/admin/cnae/edit";
import { CNAESModel } from "../../../../../../domain/models/cnae";
import { makeRemoteEconomicActivity } from "../../../../usecases/activity-economic/remote-activity-economic-factory";
import { makeRemoteCNAES } from "../../../../usecases/cnaes/remote-cnaes-factory";
import { toastUtils } from "../../../../../../utils";
import { EconomicActivitiesData } from "../../../../../../presentation/pages/admin/cnae/edit/economic-activities";

const MakeCnaeEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const edit = id ? searchParams.get("edit") === "true" : true;

  const history = useHistory();

  const [data, setData] = useState<CNAESModel>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setLoading(true);
      makeRemoteCNAES().loadById(Number(id)).then((response) => {
        setData(response);
      }).catch((error) => {
        console.error("Error fetching CNAE:", error);
      }).finally(() => {
        setLoading(false);
      });
    };
    if (!id) {
      setLoading(false);
      return;
    }

    const idNumber = Number(id);
    if (Number.isNaN(idNumber)) {
      toastUtils.showMessage({
        type: "error",
        message: "ID inválido fornecido.",
      });
      return;
    }

    fetchData();
  }, [id]);

  const goBack = (): void => {
    history.goBack();
  };

  const createActivityEconomic = async (cnaeId: number, values: any): Promise<void> => {
    try {
      await makeRemoteEconomicActivity().create({
        cnae: {
          id: cnaeId,
        },
        aliquot: values?.aliquot,
        code: values?.code,
        tributationCode: values?.tributationCode,
        description: values?.description,
        cityPattern: { id: values?.cityId },
      });
    } catch (error: any) {
      toastUtils.showMessage({
        type: "error",
        message: `Erro ao salvar atividade econômica:${error?.detail || error?.userMessage
          }`,
      });
    }
  };

  const updateActivityEconomic = async (cnaeId: number, values: any): Promise<void> => {
    try {
      await makeRemoteEconomicActivity().update(values.id, {
        cnae: {
          id: cnaeId,
        },
        aliquot: values?.aliquot,
        code: values?.code,
        tributationCode: values?.tributationCode,
        description: values?.description,
        cityPattern: { id: values?.cityId },
      });
    } catch (error: any) {
      toastUtils.showMessage({
        type: "error",
        message: `Erro ao atualizar atividade econômica:${error?.detail || error?.userMessage
          }`,
      });
    }
  };

  const create = async (values: any, ec: EconomicActivitiesData, actions: any): Promise<void> => {
    try {
      actions.setSubmitting(true);
      const cnaesResponse = await makeRemoteCNAES().create({
        code: values?.code,
        description: values?.description
      });
      if (cnaesResponse?.id) {
        ec.toCreate.forEach((activity) => {
          createActivityEconomic(cnaesResponse.id as number, activity);
        });
      }
      toastUtils.showMessage({
        type: "success",
        message: "Salvo com Sucesso",
      });
      actions.setSubmitting(false);
    } catch (error: any) {
      actions.setSubmitting(false);
      toastUtils.showMessage({
        type: "error",
        message: `Erro ao salvar informações:${error?.detail || error?.userMessage
          }`,
      });
    }
  };


  const handleSubmit = async (values: any, ec: EconomicActivitiesData, actions: any): Promise<void> => {

    if (!id) {
      await create(values, ec, actions);
      goBack();
      return;
    }
    const cnaeId = Number(id);

    if (Number.isNaN(cnaeId)) {
      toastUtils.showMessage({
        type: "error",
        message: "ID inválido fornecido.",
      });
      return;
    }

    try {
      actions.setSubmitting(true);
      await makeRemoteCNAES().update(cnaeId, {
        code: values?.code,
        description: values?.description
      });

      ec.toCreate.forEach((activity) => {
        createActivityEconomic(cnaeId, activity);
      });

      ec.toUpdate.forEach(async (activity) => {
        updateActivityEconomic(cnaeId, activity);
      });

      ec.toDelete.forEach(async (activity) => {
        makeRemoteEconomicActivity().delete(activity);
      });


      toastUtils.showMessage({
        type: "success",
        message: "Salvo com Sucesso",
      });

      actions.setSubmitting(false);
      goBack();
    } catch (error: any) {
      actions.setSubmitting(false);
      toastUtils.showMessage({
        type: "error",
        message: `Erro ao salvar informações:${error?.detail || error?.userMessage
          }`,
      });
    }
  };

  const handleEdit = (): void => {
    searchParams.set("edit", "true");
    history.replace({ search: searchParams.toString() });
  };

  return (
    <CnaeViewEdit
      onSubmit={handleSubmit}
      data={data}
      edit={edit}
      loading={loading}
      id={id}
      handleEdit={handleEdit}
    />
  );
};

export default MakeCnaeEdit;
